import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { useFocusEffect, useRouter } from 'expo-router'
import { observer } from 'mobx-react-lite'
import { Dimensions, Image, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import Swiper from 'react-native-web-swiper'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import Separator from './Separator.svg'
import { PieChart as SVGPieChart } from 'react-native-svg-charts'
import { Defs, LinearGradient, Stop, Text } from 'react-native-svg'
import { LinearGradient as ExpoLinearGradiant } from 'expo-linear-gradient'
import { mapPosition } from '../../utils/positionMapping'
import { mapPlayingStyleDescription, mapPlayingStyleIcon, parseDateStart } from './GameStatsUtils'
import useFetchLastGameStats from './useFetchLastGameStats'
import { SkeletonLoader } from '../home-screen/SkeletonLoader'
import { events } from '../../models/analytics/analytics-events'
import { formatNumberUS } from '@ppwr/poker'
import backgroundImage from '../../assets/GameStatisticsBackground.png'
import globalStyle from '../../styles/globalStyle'
import { isWeb, WebWrapper } from '../../components/WebWrapper'
import { PieChart } from '../profile-screen/PieChart'

interface CustomPieChartProps {
  color: ColorVariant
  value: number
}

type ColorVariant = 'blue' | 'orange' | 'purple'

const ColorValues: Record<ColorVariant, [string, string]> = {
  orange: ['rgb(233, 226, 56)', 'rgb(255, 114, 34)'],
  purple: ['rgb(72, 13, 147)', 'rgb(255, 62, 212)'],
  blue: ['rgb(72, 13, 147)', 'rgb(91, 196, 255)']
}

const CustomPieChart: React.FC<CustomPieChartProps> = ({ color, value }) => {
  if (isWeb) {
    return (
      <PieChart
        data={[
          { color: ColorValues[color][1], percent: value / 100 },
          { color: 'rgb(221, 218, 210)', percent: 1 - value / 100 }
        ]}
        size={80}
        strokeWidth={8}
        centerComponent={
          <span style={{ fontSize: 24, fontWeight: 500, color: 'black', fontFamily: 'Feature Deck App' }}>
            {`${Math.round(value)}%`}
          </span>
        }
      />
    )
  }

  const Gradient1 = () => (
    <Defs key={'grad'}>
      <LinearGradient id={'grad'} x1={'0%'} y1={'0%'} x2={'0%'} y2={'100%'}>
        <Stop offset={'0%'} stopColor={ColorValues[color][0]} />
        <Stop offset={'100%'} stopColor={ColorValues[color][1]} />
      </LinearGradient>
    </Defs>
  )

  const data = [
    {
      value: value,
      svg: { fill: 'url(#grad)' },
      key: 'grad-section'
    },
    {
      value: 100 - value,
      svg: { fill: 'rgb(221, 218, 210)' },
      key: 'solid-section'
    }
  ]

  return (
    // @ts-ignore
    <SVGPieChart
      style={styles.pieChartSize}
      data={data}
      // @ts-ignore
      spacing={0}
      padAngle={0}
      outerRadius={'100%'}
      innerRadius={'80%'}
      // @ts-ignore
      sort={(_, __) => undefined}
    >
      <Gradient1 />
      <Text
        x="0%"
        y="3%"
        fontSize="24"
        fontWeight="500"
        fill="black"
        fontFamily={'Feature Deck App'}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {`${Math.round(value)}%`}
      </Text>
    </SVGPieChart>
  )
}

export const GameStatistics = observer(() => {
  const router = useRouter()
  const { width } = useWindowDimensions()
  const [swipeIndex, setSwipeIndex] = useState(0)
  const [leavingGame, setLeavingGame] = useState(false)
  const {
    analytics: { setEvent },
    game: { leaveCurrentGame, myGameStats, isGameOver }
  } = useStore()

  useFocusEffect(
    useCallback(() => {
      setEvent(events.gameStats.leaderboard_displayed)
    }, [setEvent])
  )

  useEffect(() => {
    setEvent(events.gameStats.post_game_stats_screen_viewed, {
      screen_name: swipeIndex === 0 ? 'Game stats' : 'Playing style',
      index: swipeIndex
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swipeIndex])

  const isSmallerDevice = Dimensions.get('screen').height < 750

  const { userStats, loading } = useFetchLastGameStats()

  const handleClose = useCallback(async () => {
    if (router.canGoBack()) {
      router.back()
      return
    }
    router.navigate('/game')
  }, [])

  const goToPlayTab = useCallback(async () => {
    try {
      setLeavingGame(true)
      await leaveCurrentGame()
      router.navigate('/(root)/(tabs)/play')
    } catch (e) {
      setLeavingGame(false)
      console.error('An error ocurred', e)
    }
  }, [leaveCurrentGame])

  const onPressGoToPlayTab = () => {
    setEvent(events.gameStats.post_game_stats_screen_exited, {
      source: 'Play Tab button'
    })
    goToPlayTab()
  }

  const handleGoBackToTable = () => {
    if (isGameOver) {
      goToPlayTab().then().catch()
    } else {
      handleClose()
    }
  }

  const handlePressClose = () => {
    setEvent(events.gameStats.post_game_stats_screen_exited, {
      source: 'X button'
    })
    handleGoBackToTable()
  }

  const twoLinesText = (text: string) => {
    const lines = text.split('\n')
    if (lines.length < 2) {
      const words = text.split(' ')
      const mid = Math.ceil(words.length / 2)
      return `${words.slice(0, mid).join(' ')}\n${words.slice(mid).join(' ')}`
    }
    return text
  }

  const handlePressLeaderboardButton = () => {
    router.navigate('/game/leaderboard')
    setEvent(events.gameStats.leaderboard_tapped)
  }

  const StatsCard = ({ children, centerContent }: { children: ReactNode; centerContent?: boolean }) => {
    return (
      <DS.Frame
        style={[
          styles.statsCardContainer,
          centerContent ? { justifyContent: 'center' } : {},
          isSmallerDevice ? { transform: [{ scale: 0.9 }] } : {}
        ]}
      >
        <DS.Frame gap="16" style={styles.statsCard}>
          {children}
        </DS.Frame>
      </DS.Frame>
    )
  }

  return (
    <View style={styles.backgroundImage}>
      <View
        id="background-svg"
        style={[
          StyleSheet.absoluteFill,
          {
            maxWidth: 600,
            marginHorizontal: isWeb ? 'auto' : undefined
          }
        ]}
      >
        <Image
          source={backgroundImage}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%'
          }}
        />
      </View>
      <SafeAreaView style={globalStyle.container}>
        <StatusBar style={'light' as StatusBarStyle} />
        <WebWrapper>
          <DS.Frame style={styles.topNav} row>
            <TouchableOpacity onPress={handlePressClose}>
              <DS.Icon name="Close" size={24} color="Global/White" />
            </TouchableOpacity>
            <DS.Frame flex={1} alignItems="flex-end">
              <DS.Button
                type="On Light"
                variant="Small/Secondary"
                style={styles.buttonStyle}
                onPress={handlePressLeaderboardButton}
              >
                <DS.Text variant={'Mini/Bold'} color={'Global/White'}>
                  LEADERBOARD
                </DS.Text>
              </DS.Button>
            </DS.Frame>
          </DS.Frame>

          <DS.Frame style={[styles.swiperContainer, { paddingHorizontal: width > 600 ? 80 : 0 }]}>
            {loading ? (
              <StatsCard centerContent>
                <SkeletonLoader height={400} />
              </StatsCard>
            ) : (
              <Swiper
                from={0}
                loop={false}
                controlsProps={{
                  prevPos: false,
                  nextPos: false
                }}
              >
                <StatsCard centerContent>
                  <DS.Frame style={styles.gameStatsHeader}>
                    <DS.Icon name="Stars" size={88} />
                  </DS.Frame>
                  <DS.Frame gap="4" justifyContent="center" style={styles.gameStatsHeaderDate}>
                    <DS.Text variant={'Heading 1/Medium'} color="Global/White" textAlign="center">
                      Your game stats
                    </DS.Text>
                    <DS.Text variant="Body Small/Regular" color="Global/White" textAlign="center">
                      {`${myGameStats.gameType} • ${parseDateStart(myGameStats.gameStartTime)}`}
                    </DS.Text>
                  </DS.Frame>

                  <DS.Frame style={styles.statsRankContainer}>
                    <DS.Frame row style={styles.statsRankGraphSection}>
                      <DS.Frame flex={1} bg="Brand/Cream" gap={'8'} style={styles.rankPositionContainer}>
                        <ExpoLinearGradiant
                          colors={['#E9E238', '#FF7222']}
                          style={styles.rankGradient}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 0 }}
                        >
                          <DS.Frame alignItems="center" justifyContent="center" style={styles.rankTextContainer}>
                            <DS.Text variant={'Heading 1/Medium'}>{`#${userStats?.rank ?? '-'}`}</DS.Text>
                          </DS.Frame>
                        </ExpoLinearGradiant>
                        <DS.Frame gap="4">
                          <DS.Text variant="Caption/Medium">GAME RANK</DS.Text>
                          <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
                            You were the{mapPosition(userStats?.rank ?? 0)}best player in this game.
                          </DS.Text>
                        </DS.Frame>
                      </DS.Frame>

                      <DS.Frame flex={1} bg="Brand/Cream" gap={'8'} style={styles.biggestPotContainer}>
                        <DS.Frame style={styles.biggestPotNumber}>
                          <DS.Text variant={'Heading 1/Medium'} style={styles.biggestPotNumberText}>
                            {formatNumberUS(userStats?.biggestWin?.chipsWon) ?? '0'}
                          </DS.Text>
                          <DS.Icon name="ChipFilled" color={'Brand/Purple'} size={24} />
                        </DS.Frame>
                        <DS.Frame gap="4">
                          <DS.Text variant="Caption/Medium">BIGGEST POT WON</DS.Text>
                          <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
                            This was your biggest pot won during the game.
                          </DS.Text>
                        </DS.Frame>
                      </DS.Frame>
                    </DS.Frame>
                    <DS.Frame
                      bg="Brand/Cream"
                      justifyContent={'center'}
                      alignItems="center"
                      style={styles.winRateContainer}
                      gap="12"
                    >
                      <DS.Frame row gap="16" bg="Brand/Cream" justifyContent={'center'} alignItems="center">
                        <DS.Frame style={{ alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}>
                          <CustomPieChart color={'purple'} value={myGameStats?.percentage.handsWon} />
                        </DS.Frame>
                        <DS.Frame gap="4" flex={1}>
                          <DS.Text variant="Caption/Medium">WIN RATE EFFICIENCY</DS.Text>
                          <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
                            {`This shows the percentage of hands you won (${
                              myGameStats.numberOfHandsWon
                            }) out of all the hands (${myGameStats?.numberOfHands}).`}
                          </DS.Text>
                        </DS.Frame>
                      </DS.Frame>
                      <Separator />
                      <DS.Text variant={'Body Small/Regular'} textAlign="center">
                        This measures your effectiveness in the game. A higher efficiency means better decision-making
                        and strategy.
                      </DS.Text>
                    </DS.Frame>
                  </DS.Frame>

                  <DS.Text variant="Caption/Regular" color="Global/White" style={styles.swiperInfo}>
                    Swipe to see your playing style
                  </DS.Text>
                </StatsCard>

                <StatsCard centerContent>
                  <DS.Frame style={styles.secondCardHeader} justifyContent="center" alignItems="center">
                    {mapPlayingStyleIcon(myGameStats.playingStyle, 'Brand/Maroon')}
                  </DS.Frame>

                  <DS.Frame>
                    <DS.Frame
                      gap="16"
                      bg="Brand/Cream"
                      justifyContent={'center'}
                      alignItems="center"
                      style={styles.vsContainer}
                    >
                      <DS.Frame
                        row
                        style={styles.playStyleSectionContainer}
                        justifyContent="center"
                        alignItems="center"
                        gap="8"
                      >
                        <DS.Frame gap="8">
                          <CustomPieChart color={'orange'} value={myGameStats.percentage.preFlopFold} />
                          <DS.Text variant="Caption/Medium" textAlign="center">
                            {twoLinesText('PRE-FLOP FOLDS')}
                          </DS.Text>
                        </DS.Frame>
                        <DS.Text variant="Caption/Medium">VS</DS.Text>
                        <DS.Frame gap="8">
                          <CustomPieChart color={'blue'} value={myGameStats.percentage.winsAtShowdown} />
                          <DS.Text variant="Caption/Medium" textAlign="center">
                            {twoLinesText('WINS AT SHOWDOWN')}
                          </DS.Text>
                        </DS.Frame>
                      </DS.Frame>
                      <DS.Text variant="Caption/Regular" textAlign="center" color="Brand/Dark Grey">
                        Pre-flop folds show how often you fold before the flop. Wins at showdown show how often you win
                        when cards are revealed. Together, they reveal your play style.
                      </DS.Text>
                      <Separator />
                      <DS.Frame gap="4" alignItems="center">
                        <DS.Text variant="Caption/Medium" textAlign="center" color="Global/Black">
                          YOU PLAYED
                        </DS.Text>
                        <DS.Text variant="Heading 4/Medium" textAlign="center" color="Global/Black">
                          {myGameStats.playingStyle} Style
                        </DS.Text>
                        <DS.Text variant="Body Small/Regular" textAlign="center" color="Global/Black">
                          {mapPlayingStyleDescription(myGameStats.playingStyle)}
                        </DS.Text>
                        {mapPlayingStyleIcon(myGameStats.playingStyle)}
                      </DS.Frame>
                    </DS.Frame>
                  </DS.Frame>
                </StatsCard>
              </Swiper>
            )}
          </DS.Frame>

          {
            <DS.Frame style={styles.goToPlayTabButton}>
              <DS.Button
                variant={'Large/Secondary'}
                type={'On Light'}
                onPress={onPressGoToPlayTab}
                disabled={leavingGame}
              >
                {leavingGame ? 'Leaving game...' : 'Go to Play tab'}
              </DS.Button>
            </DS.Frame>
          }
        </WebWrapper>
      </SafeAreaView>
    </View>
  )
})

const styles = StyleSheet.create({
  statsCard: {
    borderWidth: 1,
    borderColor: `${DS.palette['Global/White']}24`,
    backgroundColor: `${DS.palette['Global/White']}24`,
    borderRadius: 16,
    padding: 16
  },
  statsCardContainer: {
    paddingHorizontal: 24,
    height: '100%'
  },
  swiperContainer: {
    flex: 1
  },
  gameStatsHeader: {
    position: 'absolute',
    width: '100%',
    alignItems: 'center',
    top: -24,
    left: 16
  },
  secondCardHeader: {
    height: 80,
    width: 80,
    position: 'absolute',
    alignSelf: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 40,
    top: -20,
    zIndex: 10
  },
  vsContainer: {
    padding: 16,
    borderRadius: 12,
    paddingTop: 40
  },
  blurCard: {
    position: 'absolute',
    borderRadius: 16,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  },
  topNav: {
    marginTop: 12,
    paddingHorizontal: 16
  },
  winRateContainer: {
    padding: 16,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12
  },
  biggestPotContainer: {
    marginLeft: 1,
    padding: 16,
    borderTopRightRadius: 12
  },
  pieChartSize: { height: 80, width: 80, overflow: 'visible' },
  rankPositionContainer: { marginRight: 1, padding: 16, borderTopStartRadius: 12 },
  buttonStyle: {
    width: 120,
    height: 28,
    paddingTop: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
  backgroundImage: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: DS.palette['Shades/Maroon 2']
  },
  gameStatsHeaderDate: { paddingTop: 48 },
  goToPlayTabButton: { paddingHorizontal: 24, paddingVertical: 16 },
  statsRankContainer: { borderRadius: 16 },
  statsRankGraphSection: { marginBottom: 2 },
  rankTextContainer: { backgroundColor: DS.palette['Brand/Cream'], borderRadius: 50 },
  rankGradient: { padding: 2, borderRadius: 50 },
  biggestPotNumber: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4
  },
  biggestPotNumberText: { marginTop: 4 },
  swiperInfo: { alignSelf: 'center' },
  playStyleSectionContainer: { marginBottom: 2 }
})
